import { create } from 'zustand';

// Simple, stable Zustand store without complex middleware
const useCentresStore = create((set, get) => ({
  // State
  centres: [],
  rooms: [],
  loading: false,
  error: null,

  // Actions
  createCentre: (centreData) => {
    const id = `centre_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    const centre = {
      id,
      name: centreData?.name || '',
      address: centreData?.address || '',
      region: centreData?.region || 'england',
      registrationNumber: centreData?.registrationNumber || '',
      ofstedNumber: centreData?.ofstedNumber || '',
      tuslaNumber: centreData?.tuslaNumber || '',
      inspectionRating: centreData?.inspectionRating || 'good',
      lastInspectionDate: centreData?.lastInspectionDate || '',
      nextInspectionDue: centreData?.nextInspectionDue || '',
      capacity: {
        babies: centreData?.capacity?.babies || 0,
        toddlers: centreData?.capacity?.toddlers || 0,
        preschool: centreData?.capacity?.preschool || 0,
        total: (centreData?.capacity?.babies || 0) + (centreData?.capacity?.toddlers || 0) + (centreData?.capacity?.preschool || 0)
      },
      operatingHours: {
        open: centreData?.operatingHours?.open || '07:30',
        close: centreData?.operatingHours?.close || '18:00'
      },
      emergencyContact: {
        name: centreData?.emergencyContact?.name || '',
        phone: centreData?.emergencyContact?.phone || '',
        email: centreData?.emergencyContact?.email || ''
      },
      insuranceDetails: {
        provider: centreData?.insuranceDetails?.provider || '',
        policyNumber: centreData?.insuranceDetails?.policyNumber || '',
        expiryDate: centreData?.insuranceDetails?.expiryDate || ''
      },
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    
    set(state => ({
      centres: [...state?.centres, centre]
    }));
  },

  updateCentre: (id, updates) => {
    set(state => ({
      centres: state?.centres?.map(centre => 
        centre?.id === id 
          ? { ...centre, ...updates, updatedAt: new Date()?.toISOString() }
          : centre
      )
    }));
  },

  deleteCentre: (id) => {
    set(state => ({
      centres: state?.centres?.filter(centre => centre?.id !== id)
    }));
  },

  // Room management actions
  createRoom: (roomData) => {
    const id = `room_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    const room = {
      id,
      name: roomData?.name || '',
      centreId: roomData?.centreId,
      capacity: roomData?.capacity || 0,
      ageGroup: roomData?.ageGroup || 'mixed',
      operatingHours: roomData?.operatingHours || { start: '07:30', end: '18:00' },
      isActive: true,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    
    set(state => ({
      rooms: [...state?.rooms, room]
    }));
  },

  updateRoom: (id, updates) => {
    set(state => ({
      rooms: state?.rooms?.map(room => 
        room?.id === id 
          ? { ...room, ...updates, updatedAt: new Date()?.toISOString() }
          : room
      )
    }));
  },

  deleteRoom: (id) => {
    set(state => ({
      rooms: state?.rooms?.filter(room => room?.id !== id)
    }));
  },

  // Helper functions
  getAllCentres: () => {
    const state = get();
    return state?.centres || [];
  },

  getRoomsByCentre: (centreId) => {
    const state = get();
    return state?.rooms?.filter(room => room?.centreId === centreId && room?.isActive) || [];
  },

  getCentreById: (id) => {
    const state = get();
    return state?.centres?.find(centre => centre?.id === id) || null;
  },

  getRoomById: (id) => {
    const state = get();
    return state?.rooms?.find(room => room?.id === id) || null;
  },

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  // Enhanced demo data initialization with mock children data
  initializeDemoData: () => {
    const state = get();
    
    // Only initialize if not already done
    if (state?.centres?.length > 0) return;

    const demoCentre = {
      id: 'demo_centre_1',
      name: 'Little Stars Nursery',
      address: '123 Main Street, London',
      region: 'england',
      registrationNumber: 'LS001',
      ofstedNumber: 'OF123456',
      inspectionRating: 'outstanding',
      capacity: {
        babies: 12,
        toddlers: 16,
        preschool: 24,
        total: 52
      },
      operatingHours: {
        open: '07:30',
        close: '18:00'
      },
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };

    const demoRooms = [
      {
        id: 'demo_room_1',
        name: 'Baby Room',
        centreId: 'demo_centre_1',
        capacity: 12,
        ageGroup: 'babies',
        operatingHours: { start: '07:30', end: '18:00' },
        isActive: true,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      },
      {
        id: 'demo_room_2',
        name: 'Toddler Room',
        centreId: 'demo_centre_1',
        capacity: 16,
        ageGroup: 'toddlers',
        operatingHours: { start: '07:30', end: '18:00' },
        isActive: true,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      },
      {
        id: 'demo_room_3',
        name: 'Pre-school Room',
        centreId: 'demo_centre_1',
        capacity: 24,
        ageGroup: 'preschool',
        operatingHours: { start: '07:30', end: '18:00' },
        isActive: true,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      }
    ];

    set(state => ({
      centres: [demoCentre],
      rooms: demoRooms
    }));
  }
}));

export default useCentresStore;

// Simple hook that uses stable selectors
export const useCentres = () => {
  const centres = useCentresStore(state => state?.centres);
  const rooms = useCentresStore(state => state?.rooms);
  const loading = useCentresStore(state => state?.loading);
  const error = useCentresStore(state => state?.error);
  const createCentre = useCentresStore(state => state?.createCentre);
  const updateCentre = useCentresStore(state => state?.updateCentre);
  const deleteCentre = useCentresStore(state => state?.deleteCentre);
  const createRoom = useCentresStore(state => state?.createRoom);
  const updateRoom = useCentresStore(state => state?.updateRoom);
  const deleteRoom = useCentresStore(state => state?.deleteRoom);
  const getAllCentres = useCentresStore(state => state?.getAllCentres);
  const getRoomsByCentre = useCentresStore(state => state?.getRoomsByCentre);
  const getCentreById = useCentresStore(state => state?.getCentreById);
  const getRoomById = useCentresStore(state => state?.getRoomById);
  const setLoading = useCentresStore(state => state?.setLoading);
  const setError = useCentresStore(state => state?.setError);
  const initializeDemoData = useCentresStore(state => state?.initializeDemoData);

  return {
    centres,
    rooms,
    loading,
    error,
    createCentre,
    updateCentre,
    deleteCentre,
    createRoom,
    updateRoom,
    deleteRoom,
    getAllCentres,
    getRoomsByCentre,
    getCentreById,
    getRoomById,
    setLoading,
    setError,
    initializeDemoData
  };
};